import { trpc } from "./trpc";

import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "../../../server/routers";
import { getTrpcUrl } from "./env";

/**
 * Error Logger Service — Sprint v66
 * ບໍລິການບັນທຶກຂໍ້ຜິດພາດໄປຫາ server
 *
 * Fail-safe rules:
 * 1. ຫ້າມ call ຕົວເອງຈາກ catch block ເດັດຂາດ
 * 2. Disable ຕົວເອງຫຼັງ 3 consecutive failures per session
 * 3. Dedup: error message ດຽວກັນພາຍໃນ 30 ວິ ບໍ່ສົ່ງຊ້ຳ
 * 4. ສົ່ງ plain strings ເທົ່ານັ້ນ (ບໍ່ serialize Error instance)
 */

interface ErrorLogOptions {
  type?: "client" | "server" | "system";
  severity?: "error" | "warning" | "critical";
  metadata?: any;
}

// Module-level state (ບໍ່ reset ຕາມ instance)
let _consecutiveFailures = 0;
const MAX_FAILURES = 3;
const DEDUP_WINDOW_MS = 30_000;
const _recentMessages = new Map<string, number>(); // message → timestamp

// Global error handlers execute outside React, so they cannot use hook-based
// tRPC utilities. Keep a dedicated client that mirrors the app's supported
// SuperJSON transport instead of posting an invalid raw body to a tRPC route.
const errorLogClient = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: getTrpcUrl(),
      transformer: superjson as any,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

function _isDuplicate(message: string): boolean {
  const last = _recentMessages.get(message);
  if (last && Date.now() - last < DEDUP_WINDOW_MS) return true;
  _recentMessages.set(message, Date.now());
  // Cleanup old entries to prevent memory leak
  if (_recentMessages.size > 50) {
    const cutoff = Date.now() - DEDUP_WINDOW_MS;
    for (const [k, v] of Array.from(_recentMessages.entries())) {
      if (v < cutoff) _recentMessages.delete(k);
    }
  }
  return false;
}

class ErrorLogger {
  private static instance: ErrorLogger;

  private constructor() {}

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * ບັນທຶກຂໍ້ຜິດພາດ
   */
  async logError(
    error: Error | string | unknown,
    options: ErrorLogOptions = {}
  ): Promise<void> {
    // ⭐ Guard 1: disabled after too many failures
    if (_consecutiveFailures >= MAX_FAILURES) return;

    // ⭐ Guard 2: extract plain string message (never serialize Error instance)
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "string"
        ? error
        : String(error);

    const stack =
      error instanceof Error
        ? (error.stack ?? "").slice(0, 4000)
        : undefined;

    // ⭐ Guard 3: dedup
    if (_isDuplicate(message)) return;

    try {
      await errorLogClient.errorLogs.logError.mutate({
        type: options.type ?? "client",
        severity: options.severity ?? "error",
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        metadata: options.metadata,
      });
      _consecutiveFailures = 0;
    } catch {
      _consecutiveFailures++;
      // ⭐ NEVER call logError here — would cause infinite loop
      console.warn(
        `[ErrorLogger] Telemetry failed — failures: ${_consecutiveFailures}/${MAX_FAILURES}`
      );
      if (_consecutiveFailures >= MAX_FAILURES) {
        console.warn("[ErrorLogger] Disabled for this session after repeated failures");
      }
    }
  }

  /**
   * ບັນທຶກຂໍ້ຜິດພາດຮ້າຍແຮງ
   */
  async logCritical(error: Error | string, metadata?: any): Promise<void> {
    return this.logError(error, { severity: "critical", metadata });
  }

  /**
   * ບັນທຶກຄຳເຕືອນ
   */
  async logWarning(message: string, metadata?: any): Promise<void> {
    return this.logError(message, { severity: "warning", metadata });
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();

/**
 * List of error messages to ignore (benign warnings)
 */
const IGNORED_ERRORS = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  "timeout", // socket.io timeout
  "[ErrorLogger]", // ⭐ Never log our own messages
  "[Global Error Handler]",
  "[ChunkError]",
];

/**
 * Check if an error should be ignored
 */
function shouldIgnoreError(message: string): boolean {
  return IGNORED_ERRORS.some(ignored => message.includes(ignored));
}

/**
 * Global Error Handler
 * ຈັບຂໍ້ຜິດພາດທັງໝົດທີ່ບໍ່ໄດ້ຖືກ handle
 */
export function setupGlobalErrorHandler(): void {
  // ຈັບ chunk loading errors (stale cache after deployment)
  window.addEventListener("error", (event) => {
    const src = (event.target as HTMLScriptElement)?.src || "";
    const msg = event.message || "";
    const isChunkError =
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      (src.includes("/assets/") && src.endsWith(".js"));
    if (isChunkError) {
      const reloadKey = "_chunkReloadAt";
      const lastReload = parseInt(sessionStorage.getItem(reloadKey) || "0");
      const now = Date.now();
      if (now - lastReload > 30_000) {
        sessionStorage.setItem(reloadKey, String(now));
        console.warn("[ChunkError] Stale chunk detected, reloading page...");
        window.location.reload();
      }
      return;
    }
  }, true);

  // ຈັບ unhandled promise rejections ສຳລັບ dynamic import errors
  window.addEventListener("unhandledrejection", (event) => {
    const msg = event.reason instanceof Error ? event.reason.message : String(event.reason);
    if (msg.includes("Failed to fetch dynamically imported module")) {
      const reloadKey = "_chunkReloadAt";
      const lastReload = parseInt(sessionStorage.getItem(reloadKey) || "0");
      const now = Date.now();
      if (now - lastReload > 30_000) {
        sessionStorage.setItem(reloadKey, String(now));
        console.warn("[ChunkError] Stale dynamic import, reloading page...");
        window.location.reload();
      }
      event.preventDefault();
      return;
    }
  }, true);

  // ຈັບ unhandled errors
  window.addEventListener("error", event => {
    const errorMessage = event.error?.message || event.message || "";
    if (shouldIgnoreError(errorMessage)) return;

    console.error("[Global Error Handler]", event.error);
    errorLogger.logError(event.error || event.message, {
      severity: "error",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // ຈັບ unhandled promise rejections
  window.addEventListener("unhandledrejection", event => {
    const errorMessage = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
    if (shouldIgnoreError(errorMessage)) return;

    console.error("[Unhandled Promise Rejection]", event.reason);
    errorLogger.logError(
      event.reason instanceof Error ? event.reason : String(event.reason),
      {
        severity: "error",
        metadata: { type: "unhandledRejection" },
      }
    );
  });

  // ຈັບ console.error (optional — ⭐ ຕ້ອງ guard ບໍ່ log ຕົວເອງ)
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    originalConsoleError.apply(console, args);

    const message = args
      .map(arg => (typeof arg === "object" ? JSON.stringify(arg) : String(arg)))
      .join(" ");

    // ⭐ ຫ້າມ log messages ຂອງ ErrorLogger ເອງ (ກັນ loop)
    if (!shouldIgnoreError(message)) {
      errorLogger.logWarning(message, {
        source: "console.error",
        args: args.map(arg => String(arg)),
      });
    }
  };
}
